/**
 * Servicio del cliente para inbox de notificaciones in-app.
 * Las notificaciones se CREAN solo desde Cloud Functions (rules lo enforzan).
 * El cliente puede:
 *   - subscribirse en realtime al inbox del usuario
 *   - marcar como leídas (campo `read` + `readAt`)
 *   - actualizar preferencias en `users/{uid}.notificationPrefs`
 */

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  writeBatch,
  getDocs,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { AppNotification, NotificationPrefs } from "@/types";

const COLL = "notifications";
const MAX_INBOX = 50;

export const notificationService = {
  /** Realtime: últimas N notificaciones del usuario. */
  subscribeInbox(
    uid: string,
    callback: (items: AppNotification[]) => void,
  ): () => void {
    const q = query(
      collection(db, COLL),
      where("to", "==", uid),
      orderBy("createdAt", "desc"),
      limit(MAX_INBOX),
    );
    return onSnapshot(
      q,
      (snap) => {
        const items: AppNotification[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<AppNotification, "id">),
        }));
        callback(items);
      },
      (err) => {
        console.error("[notifications] inbox snapshot error", err);
        callback([]);
      },
    );
  },

  /** Realtime: cantidad de no leídas. */
  subscribeUnreadCount(uid: string, callback: (n: number) => void): () => void {
    const q = query(
      collection(db, COLL),
      where("to", "==", uid),
      where("read", "==", false),
      limit(99),
    );
    return onSnapshot(
      q,
      (snap) => callback(snap.size),
      (err) => {
        console.error("[notifications] unread snapshot error", err);
        callback(0);
      },
    );
  },

  async markRead(id: string): Promise<void> {
    await updateDoc(doc(db, COLL, id), {
      read: true,
      readAt: serverTimestamp(),
    });
  },

  /** Marca todas las no leídas del usuario como leídas (batch). */
  async markAllRead(uid: string): Promise<number> {
    const q = query(
      collection(db, COLL),
      where("to", "==", uid),
      where("read", "==", false),
      limit(500),
    );
    const snap = await getDocs(q);
    if (snap.empty) return 0;
    const batch = writeBatch(db);
    snap.docs.forEach((d) =>
      batch.update(d.ref, { read: true, readAt: serverTimestamp() }),
    );
    await batch.commit();
    return snap.size;
  },

  /** Actualiza preferencias en users/{uid}. */
  async updatePrefs(uid: string, prefs: NotificationPrefs): Promise<void> {
    await updateDoc(doc(db, "users", uid), {
      notificationPrefs: prefs,
    });
  },
};
